import React, { useEffect, useCallback } from 'react';
import { Icon } from '@/components/ui/Icon';
import { SearchBar } from '@/components/ui/SearchBar';
import { FilterChip } from '@/components/ui/FilterChip';
import { MatchCard } from '@/components/match/MatchCard';
import { MatchSkeleton } from '@/components/match/MatchSkeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { useMatchStore, formatWeekendLabel } from '@/stores/matchStore';

const FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'u14', label: 'U14' },
  { key: 'u15', label: 'U15' },
  { key: 'u15f', label: 'U15F' },
  { key: 'u16', label: 'U16' },
  { key: 'u17', label: 'U17' },
  { key: 'u18', label: 'U18' },
  { key: 'u18f', label: 'U18F' },
  { key: 'u19', label: 'U19' },
  { key: 'u20', label: 'U20' },
  { key: 'seniors', label: 'Seniors' },
  { key: 'r1', label: 'R1' },
  { key: 'r2', label: 'R2' },
  { key: 'r3', label: 'R3' },
  { key: 'national', label: 'NATIONAL' },
];

export default function MatchesPage() {
  const colors = useTheme();
  const {
    isLoading, error, currentFilter, searchQuery, weekOffset,
    fetchMatches, setFilter, setSearchQuery, setWeekOffset, getFilteredMatches,
  } = useMatchStore();

  const filteredMatches = getFilteredMatches();

  useEffect(() => { fetchMatches(); }, []);

  const handleRefresh = useCallback(() => { fetchMatches(true); }, [fetchMatches]);

  if (error && filteredMatches.length === 0) {
    return (
      <div style={{ ...styles.container, backgroundColor: colors.background }}>
        <Header colors={colors} searchQuery={searchQuery} setSearchQuery={setSearchQuery} currentFilter={currentFilter} setFilter={setFilter} onRefresh={handleRefresh} />
        <div style={styles.errorContainer}>
          <Icon name="cloud-offline" size={48} color={Colors.primary} />
          <div style={{ fontSize: 14, fontWeight: 500, marginTop: 12, marginBottom: 16, color: colors.text }}>
            Impossible de charger les matchs
          </div>
          <button onClick={() => fetchMatches(true)} style={styles.retryButton}>
            <span style={styles.retryText}>Réessayer</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, backgroundColor: colors.background }}>
      <div style={{ flexShrink: 0, backgroundColor: colors.background }}>
        <Header colors={colors} searchQuery={searchQuery} setSearchQuery={setSearchQuery} currentFilter={currentFilter} setFilter={setFilter} onRefresh={handleRefresh} />

        <div style={{ ...styles.subheader, backgroundColor: colors.background }}>
          <div style={styles.weekNavRow}>
            <button
              type="button"
              onClick={() => setWeekOffset(-1)}
              style={{ ...styles.weekNavButton, backgroundColor: colors.surface, borderColor: colors.border }}
            >
              <Icon name="chevron-back" size={20} color={colors.text} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 600, flex: 1, textAlign: 'center', color: colors.text }}>
              {formatWeekendLabel(weekOffset)}
            </span>
            <button
              type="button"
              onClick={() => setWeekOffset(1)}
              style={{ ...styles.weekNavButton, backgroundColor: colors.surface, borderColor: colors.border }}
            >
              <Icon name="chevron-forward" size={20} color={colors.text} />
            </button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '0 16px', paddingBottom: 24 }}>
          {filteredMatches.length > 0 ? (
            filteredMatches.map((match) => <MatchCard key={match.idmatch_football} match={match} />)
          ) : isLoading ? (
            <>
              <MatchSkeleton />
              <MatchSkeleton />
              <MatchSkeleton />
            </>
          ) : (
            <EmptyState icon="search" title={searchQuery ? `Aucun match trouvé pour "${searchQuery}"` : 'Aucun match trouvé'} />
          )}
        </div>
      </div>
    </div>
  );
}

function Header({ colors, searchQuery, setSearchQuery, currentFilter, setFilter, onRefresh }: any) {
  return (
    <div style={{ padding: '8px 16px 8px 16px', borderBottom: `1px solid ${Colors.primary}15`, backgroundColor: colors.background }}>
      <div style={styles.searchRow}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SearchBar value={searchQuery} onChangeText={setSearchQuery} placeholder="Rechercher clubs ou stades..." />
        </div>
        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onRefresh}
            aria-label="Actualiser"
            style={{ ...styles.iconButton, flexShrink: 0, backgroundColor: colors.surface, border: 'none', cursor: 'pointer' }}
          >
            <Icon name="refresh" size={22} color={colors.text} />
          </button>
        </div>
      </div>
      <div style={styles.filtersContainer}>
        {FILTERS.map((f) => (
          <FilterChip key={f.key} label={f.label} isActive={currentFilter === f.key} onPress={() => setFilter(f.key)} />
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 },
  searchRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    paddingTop: 4,
    paddingBottom: 4,
  },
  iconButton: { width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  filtersContainer: { display: 'flex', overflowX: 'auto', paddingTop: 12, paddingBottom: 12, gap: 0 },
  subheader: { padding: '12px 16px' },
  weekNavRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 },
  weekNavButton: {
    width: 36, height: 36, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid', cursor: 'pointer',
  },
  errorContainer: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 },
  retryButton: {
    backgroundColor: Colors.primary, paddingLeft: 24, paddingRight: 24, paddingTop: 12, paddingBottom: 12,
    borderRadius: 24, display: 'flex', alignItems: 'center', cursor: 'pointer',
  },
  retryText: { color: '#ffffff', fontSize: 14, fontWeight: 600 },
};
